import React from 'react';

interface VectorDbOptionsProps {
  selectedProvider: string;
  settings: any;
  onSettingChange: (key: string, value: string) => void;
}

export const VectorDbOptions: React.FC<VectorDbOptionsProps> = ({
  selectedProvider,
  settings,
  onSettingChange
}) => {
  // Renderiza as opções específicas baseado no provedor selecionado
  switch (selectedProvider) {
    case 'postgresql':
      return (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Configuração PostgreSQL
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Configure a conexão com o banco PostgreSQL que possui a extensão pgvector habilitada.
                    Certifique-se de que a extensão pgvector esteja instalada e ativa no banco.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Host
              </label>
              <input
                type="text"
                value={settings?.PostgresqlHost || ''}
                onChange={(e) => onSettingChange('PostgresqlHost', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="localhost"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Endereço do servidor PostgreSQL
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Porta
              </label>
              <input
                type="number"
                value={settings?.PostgresqlPort || ''}
                onChange={(e) => onSettingChange('PostgresqlPort', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="5432"
                min="1"
                max="65535"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Porta do PostgreSQL (padrão: 5432)
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Banco
              </label>
              <input
                type="text"
                value={settings?.PostgresqlDatabase || ''}
                onChange={(e) => onSettingChange('PostgresqlDatabase', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="vectordb"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Nome do banco de dados
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Usuário
              </label>
              <input
                type="text"
                value={settings?.PostgresqlUsername || ''}
                onChange={(e) => onSettingChange('PostgresqlUsername', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="postgres"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Usuário do banco de dados
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Senha
            </label>
            <input
              type="password"
              value={settings?.PostgresqlPassword || ''}
              onChange={(e) => onSettingChange('PostgresqlPassword', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="••••••••"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Senha do usuário do banco de dados
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Requisito: Extensão pgvector
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Certifique-se de que a extensão pgvector esteja instalada no PostgreSQL:
                  </p>
                  <code className="block mt-2 p-2 bg-yellow-100 rounded text-xs">
                    CREATE EXTENSION IF NOT EXISTS vector;
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    
    case 'lancedb':
      return (
        <div className="text-center py-8 text-gray-500">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  LanceDB Configurado
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>
                    LanceDB é um banco de dados vetorial embutido que não requer configuração adicional.
                    Os dados serão armazenados localmente no sistema.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    
    case 'pinecone':
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chave da API Pinecone
              </label>
              <input
                type="password"
                value={settings?.PineconeApiKey || ''}
                onChange={(e) => onSettingChange('PineconeApiKey', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="chave_pinecone..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ambiente Pinecone
              </label>
              <input
                type="text"
                value={settings?.PineconeEnvironment || ''}
                onChange={(e) => onSettingChange('PineconeEnvironment', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="us-west1-gcp"
                required
              />
            </div>
          </div>
        </div>
      );
    
    case 'qdrant':
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL do Qdrant
              </label>
              <input
                type="url"
                value={settings?.QdrantUrl || ''}
                onChange={(e) => onSettingChange('QdrantUrl', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="http://localhost:6333"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chave da API Qdrant (opcional)
              </label>
              <input
                type="password"
                value={settings?.QdrantApiKey || ''}
                onChange={(e) => onSettingChange('QdrantApiKey', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="chave_qdrant..."
              />
            </div>
          </div>
        </div>
      );
    
    case 'chroma':
      return (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URL do Chroma
            </label>
            <input
              type="url"
              value={settings?.ChromaUrl || ''}
              onChange={(e) => onSettingChange('ChromaUrl', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="http://localhost:8000"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              URL do servidor Chroma (padrão: http://localhost:8000)
            </p>
          </div>
        </div>
      );
    
    case 'weaviate':
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL do Weaviate
              </label>
              <input
                type="url"
                value={settings?.WeaviateUrl || ''}
                onChange={(e) => onSettingChange('WeaviateUrl', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="http://localhost:8080"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chave da API Weaviate (opcional)
              </label>
              <input
                type="password"
                value={settings?.WeaviateApiKey || ''}
                onChange={(e) => onSettingChange('WeaviateApiKey', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="chave_weaviate..."
              />
            </div>
          </div>
        </div>
      );
    
    default:
      return (
        <div className="text-center py-8 text-gray-500">
          Selecione um banco vetorial para ver as opções de configuração
        </div>
      );
  }
};
